import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Tooltip,
  Paper,
  Select,
  MenuItem,
  Chip,
  alpha,
  CircularProgress,
  Divider,
  OutlinedInput,
} from "@mui/material";
import {
  Search,
  Refresh,
  ArrowBack,
  ArrowForward,
  Close,
  Add,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import Botones from "../Botones";

const csx = (fw = 500, fs = "0.75rem") => ({
  color: "text.secondary",
  fontWeight: fw,
  fontSize: fs,
});

const ibx = {
  width: 26,
  height: 26,
  borderRadius: 1,
  transition: "all 0.2s",
  "&:disabled": { opacity: 0.25 },
  "&:hover:not(:disabled)": {
    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
  },
};

const ToolbarCRUD = ({
  labelAgregar,
  placeholderBuscar,
  search,
  setSearch,
  buscando,
  onSearch,
  onClearSearch,
  onRefresh,
  onNuevo,
  cargando,
  openNuevo,
  editandoId,
  pagination,
  page,
  totalPages,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  botonesExtra = [],
}) => {
  const totalPagesCalc = totalPages || 1;
  const [pageInput, setPageInput] = useState(page + 1);

  useEffect(() => {
    setPageInput(page + 1);
  }, [page]);

  const goToPage = (val) => {
    const v = parseInt(val, 10);
    if (!isNaN(v) && v >= 1 && v <= totalPagesCalc) {
      onChangePage(v - 1);
    } else {
      setPageInput(page + 1);
    }
  };

  const btns = [
    ...(onNuevo
      ? [
          {
            tooltip: labelAgregar || "Agregar",
            funcion: onNuevo,
            disabled: cargando || openNuevo || !!editandoId,
            Icon: Add,
            color: "primary",
            id: 1,
            ocultar: false,
            tipo: "icono",
            variante: "contained",
            size: "small",
            sx: {},
          },
        ]
      : []),
    ...(!cargando
      ? [
          {
            tooltip: "Refrescar",
            funcion: onRefresh,
            disabled: false,
            Icon: Refresh,
            color: "primary",
            id: 2,
            ocultar: false,
            tipo: "icono",
            variante: "contained",
            size: "small",
            sx: {},
          },
        ]
      : []),
    ...botonesExtra,
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 0.8,
        mb: 0.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        background: (t) =>
          `linear-gradient(135deg, ${alpha(
            t.palette.background.paper,
            0.95,
          )} 0%, ${alpha(t.palette.primary.light, 0.03)} 100%)`,
        boxShadow: (t) =>
          `0 1px 3px ${alpha(t.palette.common.black, 0.04)}, 0 1px 2px ${alpha(
            t.palette.common.black,
            0.02,
          )}`,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              p: 0.4,
              px: 0.6,
              background: (t) =>
                `linear-gradient(135deg, ${alpha(
                  t.palette.background.paper,
                  0.9,
                )} 0%, ${alpha(t.palette.primary.main, 0.02)} 100%)`,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: (t) =>
                `inset 0 1px 0 ${alpha(t.palette.common.white, 0.05)}`,
            }}
          >
            <Botones botones={btns} />
            {cargando && (
              <CircularProgress
                size={14}
                thickness={4}
                sx={{ color: "primary.main", ml: 0.3 }}
              />
            )}
          </Box>
          {buscando && (
            <Chip
              label={`"${search}"`}
              onDelete={onClearSearch}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                height: 24,
                fontSize: "0.7rem",
                fontWeight: 500,
                "& .MuiChip-deleteIcon": { fontSize: "0.8rem" },
              }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, maxWidth: 380, minWidth: 180 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={placeholderBuscar || "Buscar..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSearch(e);
              }
              if (e.key === "Escape" && buscando) onClearSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      fontSize: "1.1rem",
                      color: "text.secondary",
                      opacity: 0.7,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <Tooltip title="Limpiar" arrow>
                    <IconButton
                      size="small"
                      onClick={onClearSearch}
                      edge="end"
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "error.main" },
                      }}
                    >
                      <Close sx={{ fontSize: "0.9rem" }} />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
                fontSize: "0.8rem",
                transition: "all 0.2s ease",
                "& fieldset": {
                  borderColor: "divider",
                  transition: "border-color 0.2s",
                },
                "&:hover fieldset": {
                  borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                },
                "&.Mui-focused": {
                  bgcolor: (t) => alpha(t.palette.background.paper, 1),
                  boxShadow: (t) =>
                    `0 0 0 3px ${alpha(
                      t.palette.primary.main,
                      0.1,
                    )}, 0 1px 3px ${alpha(t.palette.common.black, 0.06)}`,
                  "& fieldset": {
                    borderColor: "primary.main",
                  },
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "0.8rem",
                py: 0.5,
              },
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1.2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              px: 1,
              py: 0.2,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.action.hover, 0.15),
            }}
          >
            <Typography variant="caption" sx={csx(500, "0.7rem")}>
              {pagination?.numRows ?? 0}
            </Typography>
            <Typography
              variant="caption"
              sx={{ ...csx(400, "0.6rem"), color: "text.disabled" }}
            >
              reg.
            </Typography>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.2, borderColor: "divider" }}
            />
            <Typography
              variant="caption"
              sx={{
                ...csx(400, "0.6rem"),
                color: "text.disabled",
              }}
            >
              pág.
            </Typography>
            <Typography variant="caption" sx={csx(600, "0.7rem")}>
              {totalPages || 1}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: 0.2,
              bgcolor: (t) => alpha(t.palette.action.hover, 0.2),
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              gap: 0,
            }}
          >
            <Tooltip title="Primera página" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={cargando || page === 0}
                  onClick={() => onChangePage(0)}
                  sx={ibx}
                >
                  <FirstPage sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Anterior" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={cargando || (!pagination?.previous && page === 0)}
                  onClick={() => onChangePage(page - 1)}
                  sx={ibx}
                >
                  <ArrowBack sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
            <OutlinedInput
              size="small"
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => goToPage(pageInput)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  goToPage(pageInput);
                  e.target.blur();
                }
                if (e.key === "ArrowUp" && page > 0) {
                  e.preventDefault();
                  onChangePage(page - 1);
                }
                if (e.key === "ArrowDown" && page < totalPagesCalc - 1) {
                  e.preventDefault();
                  onChangePage(page + 1);
                }
                if (e.key === "Escape") {
                  setPageInput(page + 1);
                  e.target.blur();
                }
              }}
              inputProps={{
                sx: {
                  p: 0,
                  width: 28,
                  textAlign: "center",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "primary.main",
                },
                type: "text",
              }}
              sx={{
                height: 22,
                width: 36,
                "& .MuiOutlinedInput-input": { py: 0, px: 0.3 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                  borderRadius: 0.75,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                  borderWidth: 1,
                },
              }}
            />
            <Tooltip title="Siguiente" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={cargando || !pagination?.next}
                  onClick={() => onChangePage(page + 1)}
                  sx={ibx}
                >
                  <ArrowForward sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Última página" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={cargando || page >= (totalPages || 1) - 1}
                  onClick={() => onChangePage((totalPages || 1) - 1)}
                  sx={ibx}
                >
                  <LastPage sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Select
            value={rowsPerPage}
            onChange={onChangeRowsPerPage}
            disabled={cargando}
            size="small"
            sx={{
              fontSize: "0.75rem",
              height: 26,
              minWidth: 54,
              borderRadius: 1.5,
              "& .MuiSelect-select": {
                py: 0.3,
                px: 1,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: (t) => alpha(t.palette.primary.main, 0.3),
              },
            }}
          >
            {[10, 20, 50, 100, 500, 1000].map((v) => (
              <MenuItem key={v} value={v} sx={{ fontSize: "0.75rem", py: 0.3 }}>
                {v}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Paper>
  );
};

export default React.memo(ToolbarCRUD);
